import { handleSsoRequired } from "@/lib/ssoRedirect";

export const API_BASE = process.env.NEXT_PUBLIC_API_URL || "/api";

const TOKEN_KEY = "sf_access_token";
const REFRESH_KEY = "sf_refresh_token";

/** An HTTP error from the API, carrying the status and any machine-readable code.
 *  Subclasses Error so existing `err instanceof Error ? err.message : …` call
 *  sites keep working. Callers that need to branch on a permission refusal can
 *  check `status === 403` instead of dropping to a raw fetch. */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

/** Read tokens from storage (localStorage first, then sessionStorage). */
function getTokens() {
  if (typeof window === "undefined") return { access: null, refresh: null };
  const access =
    localStorage.getItem(TOKEN_KEY) ?? sessionStorage.getItem(TOKEN_KEY);
  const refresh =
    localStorage.getItem(REFRESH_KEY) ?? sessionStorage.getItem(REFRESH_KEY);
  return { access, refresh };
}

/** The result of a refresh attempt. When it fails, `code` carries the backend's
 *  machine-readable reason if it gave one (today only "account_suspended"), so
 *  the caller can say WHY the session ended instead of guessing. */
export type RefreshOutcome = { token: string | null; code?: string };

/** Prevent concurrent refresh attempts. */
let refreshPromise: Promise<RefreshOutcome> | null = null;

export async function tryRefreshToken(): Promise<RefreshOutcome> {
  // If a refresh is already in-flight, reuse it
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const { refresh } = getTokens();
    if (!refresh) return { token: null };

    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refresh }),
      });
      if (!res.ok) {
        const failure = await res.json().catch(() => ({}));
        // Clear a refresh token the server has definitively rejected, so we
        // don't keep retrying: either the token itself is bad (401), or the
        // account is suspended (a coded 403 — the likeliest suspension path,
        // since the access token usually expires before the user notices).
        if (res.status === 401 || failure.code === "account_suspended") {
          localStorage.removeItem(REFRESH_KEY);
          sessionStorage.removeItem(REFRESH_KEY);
        }
        return { token: null, code: failure.code };
      }

      const tokens = await res.json();
      // Store new tokens in whichever storage had the old ones
      const storage = localStorage.getItem(TOKEN_KEY) ? localStorage : sessionStorage;
      storage.setItem(TOKEN_KEY, tokens.access_token);
      storage.setItem(REFRESH_KEY, tokens.refresh_token);
      return { token: tokens.access_token as string };
    } catch {
      return { token: null };
    }
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

/** Clear all tokens and redirect to login. */
/** Bridge from a real Response to the tested, environment-free helper. */
function maybeRedirectToSso(response: Response, body: { code?: string } | null) {
  if (typeof window === "undefined") return;
  handleSsoRequired(response, body, {
    pathname: window.location.pathname,
    returnTo: window.location.pathname + window.location.search,
    navigate: (url) => {
      window.location.href = url;
    },
  });
}

function forceLogout(reason?: "suspended") {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_KEY);
  if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
    window.location.href = reason ? `/login?reason=${reason}` : "/login";
  }
}

export const ACTIVE_ORG_KEY = "sf_active_org";

/** Read the active org slug safely (SSR guard).
 *  URL-first: if the tab is on /o/<slug>/…, that slug wins (per-tab isolation).
 *  Falls back to localStorage for flat-URL landings (no cookie tier — D1 LOCKED).
 */
export function getActiveOrgSlug(): string | null {
  if (typeof window === "undefined") return null;
  const m = window.location.pathname.match(/^\/o\/([^/]+)/);
  if (m) return decodeURIComponent(m[1]);        // per-tab: the workspace URL wins
  return localStorage.getItem(ACTIVE_ORG_KEY);   // default for flat-URL landings (no cookie tier)
}

interface FetchOptions extends RequestInit {
  token?: string;
  timeoutMs?: number;
  _isRetry?: boolean; // internal: prevent infinite retry loops
}

export async function apiFetch<T>(
  path: string,
  options: FetchOptions = {}
): Promise<T> {
  const { token, headers: customHeaders, timeoutMs = 15000, _isRetry, signal: callerSignal, ...rest } = options;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((customHeaders as Record<string, string>) || {}),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // Inject active-org header so the backend RLS applies the correct org context.
  // Only set when a slug is stored — absence means "use the user's home org" (backend default).
  // A caller that names the workspace explicitly (the flat /connect/utils picker)
  // wins over the URL/remembered default.
  const activeOrgSlug = getActiveOrgSlug();
  if (activeOrgSlug && !headers["X-Active-Org"]) {
    headers["X-Active-Org"] = activeOrgSlug;
  }

  // Timeout via AbortController — combined with caller's signal if provided
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  if (callerSignal) {
    callerSignal.addEventListener("abort", () => controller.abort(), { once: true });
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      headers,
      signal: controller.signal,
      ...rest,
    });
  } catch (err) {
    clearTimeout(timeout);
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new Error("Request timed out. Please try again.");
    }
    throw new Error("Network error. Please check your connection.");
  } finally {
    clearTimeout(timeout);
  }

  // 401 is the ONLY status that means "your credential is no good".
  // The backend answers BOTH a missing and an invalid credential with 401
  // (app/api/deps.py), and NGINX rewrites nothing — its only error_page covers
  // 413/502/503/504 on the integration route. A 403 is therefore always a
  // business refusal — permission, entitlement, plan — and must never end the
  // session. The sole exception is a suspended account, handled below by its
  // explicit code. See docs/superpowers/plans/2026-09-19-403-auth-overloading.md.
  if (response.status === 401 && token && !_isRetry) {
    const { token: newToken, code } = await tryRefreshToken();
    if (newToken) {
      return apiFetch<T>(path, { ...options, token: newToken, _isRetry: true });
    }
    // Refresh failed — force logout, saying why when the server told us.
    forceLogout(code === "account_suspended" ? "suspended" : undefined);
    throw new ApiError("Session expired. Redirecting to login...", 401, code);
  }

  if (!response.ok) {
    if (response.status === 429) {
      throw new ApiError("Too many requests. Please try again shortly.", 429);
    }
    if (response.status >= 500) {
      // A storage outage is a known, temporary state with its own message (spec 2026-10-11 §5.5) — show it.
      const body = await response.json().catch(() => ({}));
      // So is the large-document memory budget being full (backend document_budget.py).
      if (response.status === 503 && (body?.code === "storage_unavailable" || body?.code === "server_busy")) {
        throw new ApiError(body.detail, 503, body.code);
      }
      throw new ApiError("Server error. Please try again later.", response.status);
    }
    const error = await response.json().catch(() => ({}));
    if (response.status === 403 && error.code === "account_suspended") {
      forceLogout("suspended");
    }
    // A `sso_required` 403 is the OTHER question — "may you do this?" — so the
    // session is left intact and the user is sent to the workspace's identity
    // provider. Deliberately NOT forceLogout: that is the bug class this file
    // was rewritten to remove.
    maybeRedirectToSso(response, error);
    throw new ApiError(
      error.detail || `API error: ${response.status}`,
      response.status,
      error.code,
    );
  }

  if (response.status === 204 || response.headers.get("content-length") === "0") {
    return undefined as T;
  }
  return response.json();
}

export async function apiUpload<T>(
  path: string,
  file: File,
  token: string,
): Promise<T> {
  const formData = new FormData();
  formData.append("file", file);

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30000);

  // Build upload headers (no Content-Type — browser sets multipart boundary)
  const uploadHeaders: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };
  const activeOrgSlugForUpload = getActiveOrgSlug();
  if (activeOrgSlugForUpload) {
    uploadHeaders["X-Active-Org"] = activeOrgSlugForUpload;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: uploadHeaders,
      body: formData,
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timeout);
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new Error("Upload timed out. Please try again.");
    }
    throw new Error("Network error. Please check your connection.");
  } finally {
    clearTimeout(timeout);
  }

  // 401 only — see the note in apiFetch. A 403 here is a business refusal
  // (quota, entitlement, permission) and must not end the session.
  if (response.status === 401) {
    const { token: newToken, code } = await tryRefreshToken();
    if (newToken) {
      // Retry the upload with new token (re-read active org at retry time)
      const retryOrgSlug = getActiveOrgSlug();
      const retryHeaders: Record<string, string> = { Authorization: `Bearer ${newToken}` };
      if (retryOrgSlug) retryHeaders["X-Active-Org"] = retryOrgSlug;
      const retryRes = await fetch(`${API_BASE}${path}`, {
        method: "POST",
        headers: retryHeaders,
        body: formData,
      });
      if (retryRes.ok) return retryRes.json();
      // 🔴 The refresh SUCCEEDED, so the session is fine — the retry was
      // refused on its own merits (file too large, wrong type, quota, a
      // permission rule). Reporting that as "Session expired" logged the user
      // out and lost their work. Adopt the response and let the error block
      // below say what the server actually said.
      if (retryRes.status !== 401) {
        response = retryRes;
      } else {
        forceLogout();
        throw new ApiError("Session expired. Redirecting to login...", 401);
      }
    } else {
      forceLogout(code === "account_suspended" ? "suspended" : undefined);
      throw new ApiError("Session expired. Redirecting to login...", 401, code);
    }
  }

  if (!response.ok) {
    if (response.status === 429) {
      throw new ApiError("Too many requests. Please try again shortly.", 429);
    }
    if (response.status >= 500) {
      // A storage outage is a known, temporary state with its own message (spec 2026-10-11 §5.5) — show it.
      const body = await response.json().catch(() => ({}));
      // So is the large-document memory budget being full (backend document_budget.py).
      if (response.status === 503 && (body?.code === "storage_unavailable" || body?.code === "server_busy")) {
        throw new ApiError(body.detail, 503, body.code);
      }
      throw new ApiError("Server error. Please try again later.", response.status);
    }
    const error = await response.json().catch(() => ({}));
    if (response.status === 403 && error.code === "account_suspended") {
      forceLogout("suspended");
    }
    // A `sso_required` 403 is the OTHER question — "may you do this?" — so the
    // session is left intact and the user is sent to the workspace's identity
    // provider. Deliberately NOT forceLogout: that is the bug class this file
    // was rewritten to remove.
    maybeRedirectToSso(response, error);
    throw new ApiError(
      error.detail || `Upload error: ${response.status}`,
      response.status,
      error.code,
    );
  }

  return response.json();
}
